/* eslint-disable max-lines */
import { useState } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'

import Alert from '@web/components/Modal/Alert'
import { useModal } from '@web/components/Modal/useModal'
import VerifyRealName from '@web/components/Modal/VerifyRealName'
import { openErrorToast, openToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import { AcceptFollowRequestMutationVariables, UseFollowMethods_OtherFragment } from '@web/gql/graphql'
import { extractByTypename } from '@web/libs/apollo/typeGuard'
import { messages } from '@web/libs/constants'
import nClicks from '@web/libs/nlog/click/codes'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'
import { UserRelationshipRequestsQuery } from '@web/pages/[objectId]/components/UserRelationship/UserRelationshipRequests'
import { UserRelationshipsQuery } from '@web/pages/[objectId]/components/UserRelationship/UserRelationships'

import { useGoTo } from './useGoTo'
import useMe from './useMe'

const MAX_FOLLOW_REQUESTS = 10000

const useFollowMethods = () => {
  const { me } = useMe()
  const { goToLogin, goTo } = useGoTo()

  const [loading, setLoading] = useState(false)
  const [getMe] = useLazyQuery(
    gql(`
      query FollowAuthorization {
        me {
          ... on MeSucceed {
            user {
              isRealName
            }
          }
        }
      }
    `),
  )

  const { openModal: openFollowLimitModal } = useModal(Alert, {
    label: {
      confirm: '확인',
    },
    clickCodes: {
      confirm: nClicks.followPopup.REQ_LIMIT_CLOSE,
    },
    message: (
      <>
        팔로우 신청 대기는
        <br />
        최대 {MAX_FOLLOW_REQUESTS.toLocaleString('kr')}명까지 가능합니다.
      </>
    ),
  })

  const errorHandler = (error: unknown) => {
    neloLogger('error', extractErrorMessage(error))
    openErrorToast(messages.ERR_FOLLOW)
    setLoading(false)
  }

  const [lazyCancelFollow] = useMutation(
    gql(`
      mutation CancelFollow($input: CancelFollowInput!) {
        cancelFollow(input: $input) {
          ... on CancelFollowSuccess {
            other {
              ...UseFollowMethods_Other
            }
            me {
              id
              stat {
                followingCount
              }
            }
          }
          ... on BaseError {
            message
          }
        }
      }
    `),
  )

  const cancelFollow = (other?: UseFollowMethods_OtherFragment, onCompleted?: () => void) => {
    if (loading) {
      openToast({ title: messages.WAIT_REQUEST })
      return
    }

    if (!other?.idno) {
      openErrorToast(messages.ERR_FOLLOW)
      return
    }

    setLoading(true)
    lazyCancelFollow({
      variables: { input: { followingIdno: other.idno } },
      onCompleted: () => {
        onCompleted?.()
        setLoading(false)
      },
      onError: errorHandler,
      refetchQueries: [UserRelationshipsQuery],
    })
  }

  const [lazyDeleteFollower] = useMutation(
    gql(`
      mutation DeleteFollower($input: CancelFollowInput!) {
        deleteFollower(input: $input) {
          ... on CancelFollowSuccess {
            other {
              ...UseFollowMethods_Other
            }
            me {
              id
              stat {
                followerCount
              }
            }
          }
          ... on BaseError {
            message
          }
        }
      }
    `),
  )

  const deleteFollower = (other?: UseFollowMethods_OtherFragment, onCompleted?: () => void) => {
    if (loading) {
      openToast({ title: messages.WAIT_REQUEST })
      return
    }

    if (!other?.idno) {
      openErrorToast(messages.ERR_FOLLOW)
      return
    }

    setLoading(true)
    lazyDeleteFollower({
      variables: { input: { followingIdno: other.idno } },
      onCompleted: () => {
        onCompleted?.()
        setLoading(false)
      },
      onError: errorHandler,
      refetchQueries: [UserRelationshipsQuery, UserRelationshipRequestsQuery],
    })
  }

  const { openModal: openVerifyRealNameModal } = useModal(VerifyRealName)

  const [lazyFollowRequest] = useMutation(
    gql(`
      mutation FollowRequest($input: FollowRequestInput!) {
        followRequest(input: $input) {
          ... on FollowRequestSuccess {
            other {
              ...UseFollowMethods_Other
            }
            me {
              id
              stat {
                followRequestCount
              }
            }
            isAutoAccept
          }
          ... on DuplicateFollowRequest {
            message
          }
          ... on BaseError {
            message
          }
        }
      }
    `),
  )

  const followRequest = async (other?: UseFollowMethods_OtherFragment, onCompleted?: () => void) => {
    if (!other?.idno) {
      openErrorToast(messages.ERR_FOLLOW)
      return
    }

    const { data } = await getMe()

    const { MeSucceed } = extractByTypename(data?.me)
    if (!MeSucceed) {
      goToLogin({ replace: true })
      return
    }

    if (!MeSucceed.user.isRealName) {
      openVerifyRealNameModal()
      return
    }

    if (me?.stat.followRequestCount && me.stat.followRequestCount >= MAX_FOLLOW_REQUESTS) {
      openFollowLimitModal()
      return
    }

    if (loading) {
      openToast({ title: messages.WAIT_REQUEST })
      return
    }

    setLoading(true)
    lazyFollowRequest({
      variables: { input: { followingIdno: other.idno } },
      onCompleted: data => {
        const {
          FollowRequestSuccess,
          DuplicateFollowRequest,
          AuthenticationError,
          FollowCommonRequestFailed,
          NotFoundOpponentUser,
        } = extractByTypename(data.followRequest)

        if (FollowRequestSuccess) {
          openToast(
            FollowRequestSuccess.isAutoAccept
              ? {
                  title: (
                    <>
                      <em>팔로잉</em>되었습니다.
                    </>
                  ),
                }
              : {
                  type2Row: true,
                  title: <em>팔로우가 신청되었습니다.</em>,
                  text: <>수락 시 네이버앱 알림으로 알려드려요.</>,
                },
          )
          onCompleted?.()
        } else if (DuplicateFollowRequest) openErrorToast(DuplicateFollowRequest.message)
        else if (AuthenticationError) openErrorToast(AuthenticationError.message)
        else if (FollowCommonRequestFailed) openErrorToast(FollowCommonRequestFailed.message)
        else if (NotFoundOpponentUser) openErrorToast(NotFoundOpponentUser.message)
        else openErrorToast(messages.ERR_COMMON)

        setLoading(false)
      },
      onError: errorHandler,
      refetchQueries: [UserRelationshipRequestsQuery],
    })
  }

  const [lazyAcceptFollowRequest] = useMutation(
    gql(`
      mutation AcceptFollowRequest($input: AcceptFollowRequestInput!) {
        acceptFollowRequest(input: $input) {
          ... on AcceptFollowRequestSuccess {
            other {
              ...UseFollowMethods_Other
            }
            me {
              id
              stat {
                followerCount
                followRequesterCount
              }
            }
          }
          ... on DuplicateAcceptFollowRequest {
            message
          }
          ... on BaseError {
            message
          }
        }
      }
    `),
  )

  const acceptFollowRequest = (other?: UseFollowMethods_OtherFragment, onCompleted?: () => void) => {
    if (loading) {
      openToast({ title: messages.WAIT_REQUEST })
      return
    }

    if (!other?.idno) {
      openErrorToast(messages.ERR_FOLLOW)
      return
    }

    setLoading(true)
    lazyAcceptFollowRequest({
      variables: { input: { followerIdno: other.idno } } as AcceptFollowRequestMutationVariables,
      onCompleted: () => {
        onCompleted?.()
        setLoading(false)
      },
      onError: errorHandler,
      refetchQueries: [UserRelationshipsQuery, UserRelationshipRequestsQuery],
    })
  }

  const [lazyRejectFollowRequest] = useMutation(
    gql(`
      mutation RejectFollowRequest($input: RejectFollowRequestInput!) {
        rejectFollowRequest(input: $input) {
          ... on RejectFollowRequestSuccess {
            other {
              ...UseFollowMethods_Other
            }
            me {
              id
              stat {
                followRequesterCount
              }
            }
          }
          ... on FollowCommonRequestFailed {
            message
          }
          ... on BaseError {
            message
          }
        }
      }
    `),
  )

  const rejectFollowRequest = (other?: UseFollowMethods_OtherFragment, onCompleted?: () => void) => {
    if (loading) {
      openToast({ title: messages.WAIT_REQUEST })
      return
    }

    if (!other?.idno) {
      openErrorToast(messages.ERR_FOLLOW)
      return
    }

    setLoading(true)
    lazyRejectFollowRequest({
      variables: { input: { followerIdno: other.idno } },
      onCompleted: () => {
        onCompleted?.()
        setLoading(false)
      },
      onError: errorHandler,
      refetchQueries: [UserRelationshipRequestsQuery],
    })
  }

  const [lazyCancelFollowRequest] = useMutation(
    gql(`
      mutation CancelFollowRequest($input: CancelFollowRequestInput!) {
        cancelFollowRequest(input: $input) {
          ... on CancelFollowRequestSuccess {
            other {
              ...UseFollowMethods_Other
            }
            me {
              id
              stat {
                followRequestCount
              }
            }
          }
          ... on DuplicateCancelFollowRequest {
            message
          }
          ... on BaseError {
            message
          }
        }
      }
  `),
  )

  const cancelFollowRequest = (other?: UseFollowMethods_OtherFragment, onCompleted?: () => void) => {
    if (loading) {
      openToast({ title: messages.WAIT_REQUEST })
      return
    }

    if (!other?.idno) {
      openErrorToast(messages.ERR_FOLLOW)
      return
    }

    setLoading(true)
    lazyCancelFollowRequest({
      variables: { input: { followingIdno: other.idno } },
      onCompleted: () => {
        onCompleted?.()
        setLoading(false)
      },
      onError: errorHandler,
      refetchQueries: [UserRelationshipRequestsQuery],
    })
  }

  return {
    followRequest,
    cancelFollowRequest,
    acceptFollowRequest,
    rejectFollowRequest,
    cancelFollow,
    deleteFollower,
    loading,
  }
}

useFollowMethods.fragments = {
  user: gql(`
    fragment UseFollowMethods_Other on User {
      id
      idno
      didIRequestFollow
      isFollowRequester
      isFriend
      stat {
        followerCount
      }
    }
  `),
}

export default useFollowMethods
